import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';

import copy from '../assets/about.json';

gsap.registerPlugin(ScrollTrigger);

function CardForAbout({title,location,duration,desc}){
  return(
    <div className='flex flex-col text-sm space-y-2 my-3 text-balance '>
      <h1 className='text-2xl font-semibold'>{title}</h1>
      <h4 className='font-semibold text-white/40'>{location} <span className='text-white/15 font-light'>{'//'}</span> {duration}</h4>
      <p className='font-light'>{desc}</p>
    </div>
  )
}

function About() {
  const pageRef = useRef(null)
  const containerRef = useRef(null);

  const [dependencybox, setDepBox] = useState(false);


  useEffect(() => {
    function enableScrollBehavior() {
      if (window.innerWidth < 1024) return;
  
      const scrollContainer = pageRef.current;
      if (!scrollContainer) return;
  
      let timeout;
      let isScrolling = false;
      let currentScrollLeft = 0;
      let targetScrollLeft = 0;
  
      // Smooth scroll logic
      function smoothScroll() {
        if (!isScrolling) return;
  
        currentScrollLeft += (targetScrollLeft - currentScrollLeft) * 0.1;
  
        if (Math.abs(targetScrollLeft - currentScrollLeft) < 0.5) {
          currentScrollLeft = targetScrollLeft;
          isScrolling = false;
        }
  
        scrollContainer.scrollLeft = currentScrollLeft;
  
        if (isScrolling) {
          requestAnimationFrame(smoothScroll);
        }
      }
  
      // Wheel scroll handler
      function handleWheel(evt) {
        evt.preventDefault();
  
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          isScrolling = false;
        }, 200);
  
        targetScrollLeft += evt.deltaY + evt.deltaX;
        targetScrollLeft = Math.max(
          0,
          Math.min(
            scrollContainer.scrollWidth - scrollContainer.clientWidth,
            targetScrollLeft
          )
        );
  
        if (!isScrolling) {
          isScrolling = true;
          smoothScroll();
        }
      }
  
      scrollContainer.addEventListener("wheel", handleWheel);
  
      return () => {
        scrollContainer.removeEventListener("wheel", handleWheel);
      };
    }
  
    enableScrollBehavior();
  
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        enableScrollBehavior();
      }
    };
  
    window.addEventListener("resize", handleResize);
  
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  

  return (
    <div ref={pageRef} className="overflow-scroll scrollbar-hide lg:overflow-hidden w-full h-full pageref">

      <div ref={containerRef} className='h-full lg:w-[2450px] px-4 py-8 lg:py-0 lg:px-24 lg:flex justify-between'>
        <div className='lg:w-[350px] text-white'>
          <h1 className="text-3xl font-bold mb-8">
            About me
          </h1>
          <p className="font-light text-sm mb-12">
            {copy.about}
          </p>
          <h1 className="text-3xl font-bold mb-8">
            Biography
          </h1>
          <p className="font-light text-sm mb-12 whitespace-pre-line">
            {copy.biography}
          </p>
        </div>
        
        <div className='lg:w-[450px] text-white mb-12'>
          <h1 className="text-3xl font-bold mb-8">
            Work & Experiences
          </h1>
          <div className='space-y-6'>
            {copy.experiences?.map((exp, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <hr className='h-[1px] w-full border-white/10'/>}
                <CardForAbout 
                  title={exp.title}
                  location={exp.location}
                  duration={exp.duration} 
                  desc={exp.desc}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
        
        <div className='lg:w-[350px] text-white mb-8'>
          <h1 className="text-3xl font-bold mb-8">
            Other projects
          </h1>

          {copy.other_projects?.map((proj, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <hr className='h-[1px] w-full border-white/10'/>}
              <div className='flex flex-col text-sm space-y-4 my-4 text-balance '>
                <h1 className='text-2xl font-semibold'>{proj.title}</h1>
                <p className='font-light'>{proj.desc}</p>
                {proj.link && (
                  <a className='text-sm font-semibold hover:text-white/30 transition-all' href={proj.link} target='_blank' rel="noreferrer">
                    More info →
                  </a>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className='lg:w-[380px] text-white'>
          <h1 className="text-3xl font-bold mb-8">
            Skills
          </h1>
          <ul className='list-disc text-sm space-y-2 text-balance font-light text-white mb-12'>
            {copy.skills?.map((skill, idx) => (
              <li 
                key={idx} 
                className='[&::marker]:text-white/50 [&::marker]:font-semibold'
                dangerouslySetInnerHTML={{
                  __html: skill.replace(/\*\*(.*?)\*\*/g, '<span class="font-bold">$1</span>')
                }}
              />
            ))}
          </ul>
          <h1 className="text-3xl font-bold mb-8">
            Services
          </h1>
          <ul className='list-disc text-sm space-y-2 font-light text-white mb-8'>
            {copy.services?.map((service, idx) => (
              <li 
                key={idx} 
                className='[&::marker]:text-white/50 [&::marker]:font-semibold'
                dangerouslySetInnerHTML={{
                  __html: service.replace(/\*\*(.*?)\*\*/g, '<span class="font-bold">$1</span>')
                }}
              />
            ))}
          </ul>
          <a className='text-sm font-semibold hover:text-white/30 transition-all' href="https://work.pyotr.xyz" target='_blank' rel="noopener noreferrer">
            More info →
          </a>
        </div>

        <div className='w-full h-[25rem] justify-center lg:w-[250px] lg:h-1/3 lg:justify-auto flex items-start lg:items-center py-8 relative'>
          <div className='relative text-xs text-white/50'>
            <p className="font-light  text-center leading-5 r">
              © {(new Date().getFullYear())} Designed and Developed <br/>by Pyotr Lye • <span className='underline underline-offset-4 cursor-help' onMouseEnter={() => setDepBox(true)} onMouseLeave={() => setDepBox(false)} >Dependencies</span>
            </p>
            <ul className='absolute w-full my-4 p-4 bg-black/10 rounded-3xl space-y-2 top-full transition-all duration-300' style={{opacity:(dependencybox ? '1' : '0'), pointerEvents: (dependencybox ? 'all' : 'none'), transform:`translateY(${(dependencybox ? '0' : '5')}px)`}}>
              <li className='mb-2 font-semibold'>
                Developed with :
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg
                  viewBox="0 0 512 512"
                  fill="currentColor"
                  height="20px"
                  width="20px"
                >
                  <path d="M410.66 180.72q-7.67-2.62-15.45-4.88 1.29-5.25 2.38-10.56c11.7-56.9 4.05-102.74-22.06-117.83-25-14.48-66 .61-107.36 36.69q-6.1 5.34-11.95 11-3.9-3.76-8-7.36c-43.35-38.58-86.8-54.83-112.88-39.69-25 14.51-32.43 57.6-21.9 111.53q1.58 8 3.55 15.93a320.85 320.85 0 00-17.77 5.6C48.46 198.9 16 226.73 16 255.59c0 29.82 34.84 59.72 87.77 77.85q6.44 2.19 13 4.07-2.13 8.49-3.77 17.17c-10 53-2.2 95.07 22.75 109.49 25.77 14.89 69-.41 111.14-37.31q5-4.38 10-9.25 6.32 6.11 13 11.86c40.8 35.18 81.09 49.39 106 34.93 25.75-14.94 34.12-60.14 23.25-115.13q-1.25-6.3-2.88-12.86 4.56-1.35 8.93-2.79c55-18.27 90.83-47.81 90.83-78-.02-29-33.52-57.01-85.36-74.9zm-129-81.08c35.43-30.91 68.55-43.11 83.65-34.39 16.07 9.29 22.32 46.75 12.22 95.88q-1 4.8-2.16 9.57a487.83 487.83 0 00-64.18-10.16 481.27 481.27 0 00-40.57-50.75q5.38-5.22 11.02-10.15zM157.73 280.25q6.51 12.6 13.61 24.89 7.23 12.54 15.07 24.71a435.28 435.28 0 01-44.24-7.13c4.24-13.72 9.46-27.97 15.56-42.47zm0-48.33c-6-14.19-11.08-28.15-15.25-41.63 13.7-3.07 28.3-5.58 43.52-7.48q-7.65 11.94-14.72 24.23t-13.58 24.88zm10.9 24.17q9.48-19.77 20.42-38.78 10.93-19 23.27-37.13c14.28-1.08 28.92-1.65 43.71-1.65s29.52.57 43.79 1.66q12.21 18.09 23.13 37t20.69 38.6Q334 275.63 323 294.73q-10.91 19-23 37.24c-14.25 1-29 1.55-44 1.55s-29.47-.47-43.46-1.38q-12.43-18.19-23.46-37.29t-20.48-38.76zM340.75 305q7.25-12.58 13.92-25.49a440.41 440.41 0 0116.12 42.32 434.44 434.44 0 01-44.79 7.65q7.62-12.09 14.75-24.48zm13.72-73.07q-6.64-12.65-13.81-25-7-12.18-14.59-24.06c15.31 1.94 30 4.52 43.77 7.67a439.89 439.89 0 01-15.37 41.39zm-98.24-107.45a439.75 439.75 0 0128.25 34.18q-28.35-1.35-56.74 0c9.33-12.34 18.88-23.79 28.49-34.18zM145.66 65.86c16.06-9.32 51.57 4 89 37.27 2.39 2.13 4.8 4.36 7.2 6.67A491.37 491.37 0 00201 160.51a499.12 499.12 0 00-64.06 10q-1.83-7.36-3.3-14.82c-9.05-46.23-3.06-81.08 12.02-89.83zm-23.41 251.85q-6-1.71-11.85-3.71c-23.4-8-42.73-18.44-56-29.81-11.88-10.19-17.9-20.36-17.9-28.6 0-17.51 26.06-39.85 69.52-55q8.19-2.85 16.52-5.21a493.54 493.54 0 0023.4 60.75 502.46 502.46 0 00-23.69 61.58zm111.13 93.67c-18.63 16.32-37.29 27.89-53.74 33.72-14.78 5.23-26.55 5.38-33.66 1.27-15.14-8.75-21.44-42.54-12.85-87.86q1.53-8 3.5-16a480.85 480.85 0 0064.69 9.39 501.2 501.2 0 0041.2 51c-2.98 2.93-6.03 5.75-9.14 8.48zm23.42-23.22c-9.72-10.51-19.42-22.14-28.88-34.64q13.79.54 28.08.54c9.78 0 19.46-.21 29-.64a439.33 439.33 0 01-28.2 34.74zm124.52 28.59c-2.86 15.44-8.61 25.74-15.72 29.86-15.13 8.78-47.48-2.63-82.36-32.72-4-3.44-8-7.13-12.07-11a484.54 484.54 0 0040.23-51.2 477.84 477.84 0 0065-10.05q1.47 5.94 2.6 11.64c4.81 24.3 5.5 46.28 2.32 63.47zm17.4-102.64c-2.62.87-5.32 1.71-8.06 2.53a483.26 483.26 0 00-24.31-60.94 481.52 481.52 0 0023.36-60.06c4.91 1.43 9.68 2.93 14.27 4.52 44.42 15.32 71.52 38 71.52 55.43 0 18.6-29.27 42.74-76.78 58.52z" />
                  <path d="M256 298.55a43 43 0 10-42.86-43 42.91 42.91 0 0042.86 43z" />
                </svg>
                <span>
                  React.js
                </span>
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  height="20px"
                  width="20px"
                >
                  <path d="M12.001 4.8c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624C13.666 10.618 15.027 12 18.001 12c3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C16.337 6.182 14.976 4.8 12.001 4.8zm-6 7.2c-3.2 0-5.2 1.6-6 4.8 1.2-1.6 2.6-2.2 4.2-1.8.913.228 1.565.89 2.288 1.624 1.177 1.194 2.538 2.576 5.512 2.576 3.2 0 5.2-1.6 6-4.8-1.2 1.6-2.6 2.2-4.2 1.8-.913-.228-1.565-.89-2.288-1.624C10.337 13.382 8.976 12 6.001 12z" />
                </svg>
                <span>
                  TailwindCSS
                </span>
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg
                  viewBox="0 0 384 512"
                  fill="currentColor"
                  height="20px"
                  width="20px"
                >
                  <path d="M14 95.792C14 42.888 56.888 0 109.793 0h164.368c52.905 0 95.793 42.888 95.793 95.792 0 33.5-17.196 62.984-43.243 80.105 26.047 17.122 43.243 46.605 43.243 80.105 0 52.905-42.888 95.793-95.793 95.793h-2.08c-24.802 0-47.403-9.426-64.415-24.891v88.263c0 53.61-44.009 96.833-97.357 96.833C57.536 512 14 469.243 14 416.207c0-33.498 17.195-62.98 43.24-80.102C31.193 318.983 14 289.5 14 256.002c0-33.5 17.196-62.983 43.242-80.105C31.197 158.776 14 129.292 14 95.792zm162.288 95.795h-66.495c-35.576 0-64.415 28.84-64.415 64.415 0 35.438 28.617 64.192 64.003 64.414l.412-.001h66.495V191.587zm31.378 64.415c0 35.575 28.839 64.415 64.415 64.415h2.08c35.576 0 64.415-28.84 64.415-64.415s-28.839-64.415-64.415-64.415h-2.08c-35.576 0-64.415 28.84-64.415 64.415zm-97.873 95.793l-.412-.001c-35.386.221-64.003 28.975-64.003 64.413 0 35.445 29.225 64.415 64.931 64.415 36.282 0 65.979-29.436 65.979-65.455v-63.372h-66.495zm0-320.417c-35.576 0-64.415 28.84-64.415 64.414 0 35.576 28.84 64.415 64.415 64.415h66.495V31.377h-66.495zm97.873 128.829h66.495c35.576 0 64.415-28.839 64.415-64.415 0-35.575-28.839-64.414-64.415-64.414h-66.495v128.829z" />
                </svg>
                <span>
                  Figma
                </span>
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  height="20px"
                  width="20px"
                >
                  <path d="M.38 0a.268.268 0 00-.256.332l2.894 11.716a.268.268 0 00.01.04l2.89 11.708a.268.268 0 00.447.128L23.802 7.15a.268.268 0 00-.112-.45l-5.784-1.667a.268.268 0 00-.123-.035L6.38 1.715a.268.268 0 00-.144-.04L.456.01A.268.268 0 00.38 0zm.374.654L5.71 2.08 1.99 5.664zM6.61 2.34l4.864 1.4-3.65 3.515zm-.522.12l1.217 4.926-4.877-1.4zm6.28 1.538l4.878 1.404-3.662 3.53zm-.52.13l1.208 4.9-4.853-1.392zm6.3 1.534l4.947 1.424-3.715 3.574zm-.524.12l1.215 4.926-4.876-1.398zm-15.432.696l4.964 1.424-3.726 3.586zM8.047 8.15l4.877 1.4-3.66 3.527zm-.518.137l1.236 5.017-4.963-1.432zm6.274 1.535l4.965 1.425-3.73 3.586zm-.52.127l1.235 5.012-4.958-1.43zm-9.63 2.438l4.873 1.406-3.656 3.523zm5.854 1.687l4.863 1.403-3.648 3.51zm-.54.04l1.214 4.927-4.875-1.4zm-3.896 4.02l5.037 1.442-3.782 3.638z" />
                </svg>
                <span>
                  Three.js / Three Fiber
                </span>
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg xmlns="http://www.w3.org/2000/svg" width="20px" fill="currentColor" viewBox="0 0 82 30">
                  <path  d="M23.81 14.013v.013l-1.075 4.665c-.058.264-.322.458-.626.458H20.81a.218.218 0 0 0-.208.155c-1.198 4.064-2.82 6.858-4.962 8.535-1.822 1.428-4.068 2.093-7.069 2.093-2.696 0-4.514-.867-6.056-2.578C.478 25.09-.364 21.388.146 16.926 1.065 8.549 5.41.096 13.776.096c2.545-.023 4.543.762 5.933 2.33 1.47 1.657 2.216 4.154 2.22 7.421a.55.55 0 0 1-.549.536h-6.13a.42.42 0 0 1-.407-.41c-.05-2.259-.72-3.36-2.052-3.36-2.35 0-3.736 3.19-4.471 4.959-1.027 2.47-1.55 5.152-1.447 7.824.049 1.244.249 2.994 1.43 3.718 1.047.643 2.541.217 3.446-.495.904-.711 1.632-1.942 1.938-3.065.043-.156.046-.277.005-.332-.043-.055-.162-.068-.253-.068h-1.574a.572.572 0 0 1-.438-.202.42.42 0 0 1-.087-.362l1.076-4.674c.053-.24.27-.42.537-.453v-.011h10.33c.024 0 .049 0 .072.005.268.034.457.284.452.556h.002Z"/>
                  <path  d="M41.594 8.65a.548.548 0 0 1-.548.531H35.4c-.37 0-.679-.3-.679-.665 0-1.648-.57-2.45-1.736-2.45s-1.918.717-1.94 1.968c-.025 1.395.764 2.662 3.01 4.84 2.957 2.774 4.142 5.232 4.085 8.48C38.047 26.605 34.476 30 29.042 30c-2.775 0-4.895-.743-6.305-2.207-1.431-1.486-2.087-3.668-1.95-6.485a.548.548 0 0 1 .549-.53h5.84a.55.55 0 0 1 .422.209.48.48 0 0 1 .106.384c-.065 1.016.112 1.775.512 2.195.256.272.613.41 1.058.41 1.079 0 1.711-.763 1.735-2.09.02-1.148-.343-2.155-2.321-4.19-2.555-2.496-4.846-5.075-4.775-9.13.042-2.351.976-4.502 2.631-6.056C28.294.868 30.687 0 33.465 0c2.783.02 4.892.813 6.269 2.359 1.304 1.466 1.932 3.582 1.862 6.29h-.002Z"/>
                  <path  d="m59.096 29.012.037-27.932a.525.525 0 0 0-.529-.533h-8.738c-.294 0-.423.252-.507.42L36.707 28.842v.005l-.005.006c-.14.343.126.71.497.71h6.108c.33 0 .548-.1.656-.308l1.213-2.915c.149-.388.177-.424.601-.424h5.836c.406 0 .415.008.408.405l-.131 2.71a.525.525 0 0 0 .529.532h6.17a.522.522 0 0 0 .403-.182.458.458 0 0 0 .104-.369Zm-10.81-9.326c-.057 0-.102-.001-.138-.005a.146.146 0 0 1-.13-.183c.012-.041.029-.095.053-.163l4.377-10.827c.038-.107.086-.212.136-.314.071-.145.157-.155.184-.047.023.09-.502 11.118-.502 11.118-.041.413-.06.43-.467.464l-3.509-.041h-.008l.003-.002Z"/>
                  <path  d="M71.545.547h-4.639c-.245 0-.52.13-.585.422l-6.455 28.029a.423.423 0 0 0 .088.364.572.572 0 0 0 .437.202h5.798c.311 0 .525-.153.583-.418 0 0 .703-3.168.704-3.178.05-.247-.036-.439-.258-.555-.105-.054-.209-.108-.312-.163l-1.005-.522-1-.522-.387-.201a.186.186 0 0 1-.102-.17.199.199 0 0 1 .198-.194l3.178.014c.95.005 1.901-.062 2.836-.234 6.58-1.215 10.95-6.485 11.076-13.656.107-6.12-3.309-9.221-10.15-9.221l-.005.003Zm-1.579 16.68h-.124c-.278 0-.328-.03-.337-.04-.004-.007 1.833-8.073 1.834-8.084.047-.233.045-.367-.099-.446-.184-.102-2.866-1.516-2.866-1.516a.188.188 0 0 1-.101-.172.197.197 0 0 1 .197-.192h4.241c1.32.04 2.056 1.221 2.021 3.237-.061 3.492-1.721 7.09-4.766 7.214Z"/>
                </svg>
                <span>
                  GSAP
                </span>
              </li>
              <li className='inline-flex items-center space-x-2 h-5'>
                <svg height="20px"
                  width="20px" viewBox="0 0 600 600" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_135_831)">
                  <path d="M518.293 327.327V327.152C518.29 327.087 518.29 327.021 518.293 326.955C513.042 292.744 499.418 261.23 481.346 232.883C479.073 229.299 476.709 225.776 474.292 222.275C473.12 220.578 471.93 218.896 470.723 217.229C447.484 184.98 374.082 97.3028 335.522 51.5891C320.935 34.2901 311.335 23 311.335 23C306.986 26.4856 302.735 30.0822 298.582 33.79L298.438 33.9188C268.366 62.0273 243.429 95.1671 224.749 131.848L224.689 131.961C221.092 139.034 217.733 146.232 214.611 153.557C207.388 170.525 201.518 188.038 197.054 205.931C194.347 216.746 192.154 227.751 190.477 238.945C181.248 237.435 171.926 236.559 162.578 236.323C160.809 236.278 159.041 236.255 157.273 236.255C141.997 236.244 126.767 237.947 111.87 241.332C102.752 256.057 95.4109 271.812 90.0022 288.266C82.691 310.534 78.9771 333.826 79.0001 357.264C79.0001 464.392 155.334 553.706 256.581 573.747C270.78 576.56 285.221 577.974 299.696 577.968C302.553 577.968 305.394 577.907 308.228 577.809C315.669 577.516 323.006 576.869 330.24 575.869C347.097 573.532 363.625 569.245 379.493 563.094C383.721 561.457 387.883 559.689 391.98 557.79C467.753 522.851 520.392 446.199 520.392 357.264C520.363 347.249 519.662 337.248 518.293 327.327ZM228.462 525.692C206.732 516.512 186.992 503.2 170.337 486.495C153.636 469.84 140.327 450.103 131.147 428.377C121.624 405.87 116.743 381.673 116.796 357.234C116.742 332.793 121.623 308.593 131.147 286.083C132.662 282.466 134.302 278.915 136.065 275.43C143.07 274.551 150.123 274.111 157.182 274.111C167.114 274.11 177.025 274.988 186.802 276.733C186.59 281.666 186.484 286.644 186.484 291.637C186.35 384.538 222.981 473.718 288.376 539.703C267.751 538.489 247.486 533.75 228.462 525.692ZM309.539 507.06C305.104 502.347 300.838 497.512 296.741 492.557C277.101 468.917 261.052 442.512 249.11 414.192C232.7 375.411 224.285 333.717 224.37 291.607C224.37 290.46 224.37 289.316 224.37 288.175C243.143 296.502 260.212 308.232 274.714 322.773C297.198 345.136 312.765 373.502 319.556 404.478C322.124 416.25 323.414 428.263 323.405 440.311C323.476 463.278 318.772 486.008 309.592 507.06H309.539ZM482.612 363.561C481.454 397.93 470.608 431.273 451.325 459.747C435.654 482.871 414.924 502.124 390.707 516.046L390.464 515.933C384.1 519.579 377.521 522.838 370.763 525.692C362.345 529.254 353.67 532.175 344.811 534.429C343.235 534.833 341.654 535.212 340.068 535.565C339.212 535.762 338.363 535.952 337.507 536.126C337.84 535.52 338.166 534.891 338.484 534.27C338.977 533.323 339.462 532.375 339.932 531.421C345.716 519.852 350.375 507.753 353.843 495.292C358.821 477.405 361.337 458.923 361.322 440.357C361.329 426.85 360.002 413.375 357.359 400.129C343.319 329.918 293.263 272.725 227.28 248.583C230.32 226.251 235.774 204.316 243.548 183.161C245.291 178.428 247.148 173.727 249.118 169.06C252.088 162.028 255.301 155.133 258.756 148.374C258.777 148.34 258.794 148.305 258.809 148.268C260.226 145.32 261.688 142.418 263.204 139.524C275.098 116.875 290.091 95.9959 307.751 77.4882L308.046 77.8445C323.701 96.4088 344.652 121.384 365.52 146.548C376.196 159.429 386.85 172.363 396.761 184.525C415.348 207.34 431.321 227.435 439.952 239.369C441.187 241.082 442.392 242.787 443.574 244.514C455.97 262.51 465.548 280.704 472.041 298.7C476.17 310.046 479.147 321.779 480.93 333.722V333.873C482.388 343.698 482.956 353.634 482.627 363.561H482.612Z" fill="currentColor"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_135_831">
                  <rect width="600" height="600" fill="currentColor"/>
                  </clipPath>
                  </defs>
                </svg>
                <span>
                  FireCMS / Firebase
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
